/**
 * Trade event → "does the server's streak need a re-read?" predicate.
 *
 * The streak is server-owned: the client never computes streak numbers. A trade
 * (BC `MarketTrade`, or AMM activity of kind TRADE) whose day, in the account's
 * `streakTimezone`, differs from the cached `streakLastLocalDate` may have
 * advanced or restarted the streak, so the runtime fetches the profile once.
 * The event's chain timestamp `ts` (unix seconds) is the trade time — never
 * "now" — and the account zone is never the device zone. Without a usable
 * zone the day cannot be judged, so the answer is true. A projection that
 * carries neither streak field does not display the streak: always false.
 */

/** BC `MarketTrade` has no `kind`; AMM activity must be a TRADE (enum value 1). */
function isTradeEvent(raw: { kind?: unknown }): boolean {
  return raw.kind === undefined || Number(raw.kind) === 1;
}

export function streakDayChanged(
  prev: { streakLastLocalDate?: string | null; streakTimezone?: string | null },
  raw: { kind?: unknown; ts?: unknown },
): boolean {
  if (prev.streakLastLocalDate === undefined && prev.streakTimezone === undefined) return false;
  if (!isTradeEvent(raw)) return false;
  const seconds = eventSeconds(raw.ts);
  if (seconds === null) return false;
  const eventDay = dayInZone(seconds, prev.streakTimezone);
  return eventDay === null || eventDay !== (prev.streakLastLocalDate ?? null);
}

function eventSeconds(ts: unknown): number | null {
  const seconds = typeof ts === 'bigint' ? Number(ts) : ts;
  if (typeof seconds !== 'number' || !Number.isSafeInteger(seconds) || seconds <= 0) return null;
  return Number.isNaN(new Date(seconds * 1000).getTime()) ? null : seconds;
}

/** 'YYYY-MM-DD' of the instant in an IANA zone; null for a missing or invalid zone. */
function dayInZone(seconds: number, timeZone: string | null | undefined): string | null {
  if (typeof timeZone !== 'string' || timeZone === '') return null;
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date(seconds * 1000));
  } catch {
    return null;
  }
}
